import React, { useEffect, useMemo, useState, useRef } from "react";
import { ConnectedProps, connect } from "react-redux";
import * as Actions from "us.collection.debtor/actions";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { DebtorInfoReportStatus } from "us.collection.debtor/components/DebtorBasicInfo/Constants";
import { Formik } from "formik";
import { RootState } from "us.helper/types";
import {
  DEFAULT_DUPLICATE_DEBTOR_FILTERS,
  DebtorFilterBy,
  DEBTOR_BASIC_INFO_LABELS,
  DEBTOR_ADDRESS_INFO_LABELS,
} from "./Constants";
import { getRetainingDebtor, filterDuplicateDebtors } from "./Functions";
import { RetainDebtor, DuplicateDebtor, MergeHistoryTable } from "./Components";
import _ from "lodash";
import "./Home.scss";
import { $Drawer, $Popconfirm, WithFeatureFlag } from "us.common/components";
import { SaveSelectedDebtors } from "./Repository";
import { useFeatureFlag } from "us.common/FeatureFlagContext";
import { FeatureFlags } from "us.common/constants";

const { $Skeleton, $Button, $CheckboxGroup, $Badge, $Alert, $Empty, $Divider } =
  Common.Components;

/**
 * @description - Component to display duplicating debtors.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/UnicornBOX/pages/3627384863/Handle+Duplicate+Debtors+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/08/2024
 */
const DuplicateDebtors: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.DEBTOR"]);

  const isFeatureEnabled = useFeatureFlag(FeatureFlags.MERGE_DEBTOR);

  const {
    duplicatingDebtors,
    metaData,
    selectedDebtors,
    searchDuplicateDebtors,
    mergeSelectedDebtors,
    isDebtorMergeSaving,
  } = props;
  const { debtorId, caseId } = metaData.data ?? {};

  const firstDivRef = useRef<any>();
  const secondDivRef = useRef<any>();

  const [filterValues, setFilterValues] = useState<
    typeof DEFAULT_DUPLICATE_DEBTOR_FILTERS
  >(DEFAULT_DUPLICATE_DEBTOR_FILTERS);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Search duplicating debtors by providing the current debtorId
  useEffect(() => {
    if (metaData.data) {
      searchDuplicateDebtors && searchDuplicateDebtors({ debtorId });
    }
  }, [metaData]);

  // Get selected retaining debtor information
  const retainingDebtor = useMemo(() => {
    return getRetainingDebtor(duplicatingDebtors.data);
  }, [duplicatingDebtors.data]);

  // Get all duplicating debtors
  const allDuplicatingDebtors = useMemo(() => {
    return filterDuplicateDebtors(duplicatingDebtors.data, filterValues);
  }, [duplicatingDebtors.data, filterValues]);

  // Checkbox onchange handler to handle checkbox changes
  const handleDebtorSearch = (checkedValue: Array<any>) => {
    if (checkedValue?.length > 0) setFilterValues(checkedValue);
  };

  // Scroll label column and duplicate debtors columns together
  const handleScrollFirst = (scroll: any) => {
    secondDivRef.current.scrollTop = scroll.target.scrollTop;
  };
  const handleScrollSecond = (scroll: any) => {
    firstDivRef.current.scrollTop = scroll.target.scrollTop;
  };

  // Merge selected debtors
  const handleSubmit = () => {
    const saveParams = SaveSelectedDebtors.call({
      duplicatingDebtors: duplicatingDebtors.data,
      selectedDebtors,
      debtorId,
      caseId,
      retainingDebtor,
    });
    mergeSelectedDebtors && mergeSelectedDebtors(saveParams);
  };

  return (
    <Formik
      initialValues={{
        reportStatus: DebtorInfoReportStatus.ALL,
      }}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {() => (
        <>
          <div className="merge-debtors">
            <div className="merge-debtors-header">
              <div className="d-flex flex-fill justify-content-between mb-2">
                <div>
                  <div className="d-flex">
                    <strong className="mr-2">
                      {t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.DUPLICATE_DEBTORS"
                      )}
                    </strong>
                    {allDuplicatingDebtors?.length > 0 && (
                      <$Badge
                        count={
                          retainingDebtor
                            ? allDuplicatingDebtors?.length
                            : allDuplicatingDebtors?.length - 1
                        }
                        style={{ backgroundColor: "#1d92ff", color: "#fff" }}
                      />
                    )}
                    <$Divider className="bui-devider" type="vertical" />
                    <span className="text-muted mr-3">
                      {t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.FIND_DUPLICATE_DEBTORS_USING"
                      )}
                    </span>
                    <$CheckboxGroup
                      options={[
                        {
                          label: t("US.COLLECTION.COMMON:COMMON.NAME"),
                          value: DebtorFilterBy.NAME,
                        },
                        {
                          label: t("US.COLLECTION.COMMON:COMMON.NIN"),
                          value: DebtorFilterBy.PERSON_NO,
                        },
                        {
                          label: t("US.COLLECTION.COMMON:COMMON.POSTAL_CODE"),
                          value: DebtorFilterBy.ZIP_CODE,
                        },
                      ]}
                      value={filterValues}
                      onChange={handleDebtorSearch}
                    />

                    <$Divider className="bui-devider" type="vertical" />
                    <$Button
                      onClick={() => {
                        setDrawerVisible(true);
                      }}
                      size="small"
                      className="mr-3"
                      disabled={!isFeatureEnabled}
                    >
                      {t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.MERGE_HISTORY"
                      )}
                    </$Button>
                    <$Popconfirm
                      title={t(
                        "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.ARE_YOU_SURE_TO_MERGE_THE_SELECTED_DEBTORS?"
                      )}
                      placement="topLeft"
                      onConfirm={handleSubmit}
                      okText={t("COMMON.YES")}
                      cancelText={t("COMMON.NO")}
                      disabled={
                        selectedDebtors?.length == 0 || !isFeatureEnabled
                      }
                    >
                      <$Button
                        size="small"
                        type="primary"
                        disabled={
                          selectedDebtors?.length == 0 || !isFeatureEnabled
                        }
                        loading={isDebtorMergeSaving}
                      >
                        {t(
                          "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.MERGE_SELECTED"
                        )}
                      </$Button>
                    </$Popconfirm>
                  </div>
                </div>
              </div>
            </div>
            <$Alert
              className="mb-3"
              message={t(
                "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.RETAINING_DEBTORS_ARE_SUGGESTED_BASED_ON_THE_DEBTOR"
              )}
              type="info"
              showIcon
            />
            <$Skeleton
              loading={duplicatingDebtors.isLoading}
              active
              paragraph={{
                rows: 2,
              }}
            >
              <div
                className={`merge-debtors-body ${
                  retainingDebtor ? "mdb-with-retaining" : ""
                }`}
              >
                <div
                  className="merge-debtors-scroll-sync"
                  onScroll={handleScrollFirst}
                  ref={firstDivRef}
                >
                  <div className="mdb-col mdb-col-label">
                    <div className="mdb-col-wrap">
                      <div className="mdb-row mdb-row-header">
                        {t(
                          "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.BASIC_INFORMATION"
                        )}
                      </div>
                      {DEBTOR_BASIC_INFO_LABELS.map((label, index) => (
                        <div className="mdb-row" key={index}>
                          {t(label)}
                        </div>
                      ))}
                      <div className="mdb-row mdb-row-header">
                        {t(
                          "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.ADDRESS_INFORMATION"
                        )}
                      </div>
                      {DEBTOR_ADDRESS_INFO_LABELS.map((label, index) => (
                        <div className="mdb-row" key={index}>
                          {t(label)}
                        </div>
                      ))}
                    </div>
                  </div>
                  {retainingDebtor && <RetainDebtor />}
                </div>
                <div
                  className="merge-debtors-scroll"
                  onScroll={handleScrollSecond}
                  ref={secondDivRef}
                >
                  {allDuplicatingDebtors?.length >= 1 && (
                    <>
                      {allDuplicatingDebtors?.map((debtor, index: number) => (
                        <DuplicateDebtor
                          debtor={debtor}
                          retainingDebtor={retainingDebtor}
                          index={index}
                          showDuplicateCount={allDuplicatingDebtors?.length}
                        />
                      ))}
                    </>
                  )}
                  {!retainingDebtor && allDuplicatingDebtors?.length <= 1 && (
                    <div className="mdb-col mdb-col-no-duplicates pb-5 mb-5">
                      <$Empty
                        description={t(
                          "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.NO_DUPLICATE_DEBTORS_AVAILABLE"
                        )}
                        image={$Empty.PRESENTED_IMAGE_DEFAULT}
                      />
                    </div>
                  )}
                </div>
              </div>
            </$Skeleton>
          </div>
          <$Drawer
            title={t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.HISTORY")}
            width={1300}
            open={drawerVisible}
            onClose={() => {
              setDrawerVisible(false);
            }}
            destroyOnClose
          >
            <MergeHistoryTable onClose={() => setDrawerVisible(false)} />
          </$Drawer>
        </>
      )}
    </Formik>
  );
};

const mapState = (state: RootState) => {
  const { common, domainView, duplicateDebtors } = state;
  const { duplicatingDebtors, selectedDebtors, isDebtorMergeSaving } =
    duplicateDebtors;
  const { metaData } = domainView;

  return {
    metaData,
    currentLanguage: common.currentLanguage,
    duplicatingDebtors,
    selectedDebtors,
    isDebtorMergeSaving,
  };
};

const { debtors, mergeDebtors } = Actions.duplicateDebtors;

const mapDispatch = {
  searchDuplicateDebtors: debtors.search,
  selectDuplicateDebtors: debtors.select,
  changeRetainingDebtor: debtors.changeRetaining,
  mergeSelectedDebtors: mergeDebtors.merge,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DuplicateDebtors);
